import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAllVolunteers, updateVolunteer } from '../services/api';
import { Volunteer } from '../models/types';

const RoleManagementPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    fetchVolunteers();
  }, []);

  const fetchVolunteers = async () => {
    try {
      setLoading(true);
      const data = await getAllVolunteers();
      setVolunteers(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching volunteers:', err);
      setError('Fehler beim Laden der Helfer-Liste.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (volunteerId: string, newRole: string) => {
    try {
      setUpdating(volunteerId);
      setError(null);
      setSuccess(null);

      const volunteer = volunteers.find(v => v.id === volunteerId);
      if (!volunteer) return;

      await updateVolunteer(volunteerId, {
        name: volunteer.name,
        email: volunteer.email,
        phoneNumber: volunteer.phoneNumber,
        commodity: volunteer.commodity,
        role: newRole
      });

      // Update local state
      setVolunteers(prev => 
        prev.map(v => 
          v.id === volunteerId 
            ? { ...v, role: newRole }
            : v
        )
      );

      setSuccess(`Rolle für ${volunteer.name} erfolgreich auf "${newRole}" geändert.`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating volunteer role:', err);
      setError('Fehler beim Aktualisieren der Rolle.');
    } finally {
      setUpdating(null);
    }
  };

  const getRoleChipColor = (role: string) => {
    switch (role) {
      case 'Admin':
        return 'error';
      case 'JobManager':
        return 'warning';
      case 'QRCodeManager':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Sie haben keine Berechtigung, diese Seite aufzurufen.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Helfer-Liste...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Rollen-Verwaltung
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Verwalten Sie die Rollen der registrierten Helfer. Job-Manager können die Job-Manager-Seite nutzen 
        und Registrierungen bearbeiten. QR-Code-Manager können QR-Codes verwalten und scannen. Administratoren haben vollen Zugriff.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 3
      }}>
        {volunteers.map((volunteer) => (
          <Card key={volunteer.id}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {volunteer.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {volunteer.email}
                </Typography>
                
                {volunteer.commodity && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {volunteer.commodity}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="body2">Aktuelle Rolle:</Typography>
                  <Chip 
                    label={volunteer.role || 'Volunteer'} 
                    size="small"
                    color={getRoleChipColor(volunteer.role || 'Volunteer')}
                  />
                </Box>

                <FormControl fullWidth size="small">
                  <InputLabel>Neue Rolle</InputLabel>
                  <Select
                    value={volunteer.role || 'Volunteer'}
                    label="Neue Rolle"
                    onChange={(e) => handleRoleChange(volunteer.id, e.target.value)}
                    disabled={updating === volunteer.id}
                  >
                    <MenuItem value="Volunteer">Volunteer</MenuItem>
                    <MenuItem value="JobManager">Job Manager</MenuItem>
                    <MenuItem value="QRCodeManager">QR-Code Manager</MenuItem>
                    <MenuItem value="Admin">Administrator</MenuItem>
                  </Select>
                </FormControl>

                {updating === volunteer.id && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2">Aktualisiere...</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
        ))}
      </Box>
    </Container>
  );
};

export default RoleManagementPage;
